import setPublicPath from 'src/public-path';
import React, { Component } from 'react';
import { Link, RouteComponentProps } from 'react-router-dom';

import type { StoryModel } from 'types/story';
import type { SetStoryPayload } from 'client/actions/set-story';
import { PARAMS } from 'common/constants';
import { location } from 'common/utils/url-helper';
import { parseURL, searchObjectToString } from 'common/utils/parse-url';
import { StoryFacade } from 'common/utils/facades/story-facade';
import { getIsEditor } from 'common/utils/get-is-editor';
import GlobalStyles from 'common/components/GlobalStyles';
import Loader from 'common/components/Loader';
import NotFound from 'common/components/NotFound';

import { STORY_PATH } from 'client/constants/routes';
import { CARD_ELEMENTS_ID, STORY_ROOT_ID } from 'client/constants/common';
import { StoryHistory } from 'client/utils';
import Fade from 'client/components/common/Fade';
import SelectionHint from 'client/components/common/SelectionHint';
import SelectionHintProvider from 'client/components/common/SelectionHint/SelectionHintProvider';
import ResponsiveProvider from 'client/components/common/ResponsiveProvider';
import { ApiEvents } from 'client/ApiEvents';
import Credit from 'client/components/common/Credit';
import User from 'client/components/common/User';
import StoryElements from './StoryElements';
import CardsManager from './CardsManager';
import EmbedingConnect from './EmbedingConnect';
import css from './Story.scss';
import StoryFetcher from './StoryFetcher';
import GoogleFonts from './GoogleFonts';
import CustomFonts from './CustomFonts';
import EventProvider from './EventProvider';
import InactiveStory from './InactiveStory';
import ScrollToElement from './ScrollToElement';

const ScrollbarFix = React.lazy(() => import('./ScrollbarFix'));

const { isEmbedDevPreview, isPreviewRoute, isPreview } = location.client;

type State = {
	story?: StoryModel;
	isReachedPlanLimit?: boolean;
	version: (typeof StoryFacade.VERSIONS)[keyof typeof StoryFacade.VERSIONS];
};

type Props = RouteComponentProps & {
	story?: SetStoryPayload;
};

class Story extends Component<Props, State> {
	state: State = {
		isReachedPlanLimit: false,
		story: undefined,
		version: StoryFacade.VERSIONS.latest,
	};

	isEditor = getIsEditor();

	componentDidMount() {
		this.props.history.listen(setPublicPath);
	}

	get story() {
		return this.state.story ? new StoryFacade(this.state.story, this.state.version) : null;
	}

	onFetch = (state: EitherField<{ story: StoryModel; isReachedPlanLimit: boolean }>) => {
		if (state.story) {
			const isAdminPreview = isPreview && !this.isEditor;
			const oncePerUser = new StoryFacade(state.story).settings.restrictions?.oncePerUser;
			const shouldClearLastPlay = isAdminPreview ? true : !oncePerUser;
			if (shouldClearLastPlay) new StoryHistory({ storyId: state.story.id }).clearLastPlay();
		}
		this.setState(state);
	};

	onStoryClick = () => {
		if (!this.story) {
			return;
		}

		ApiEvents.sendStartStoryEvent({
			organizationId: this.story.base.organizationId,
			storyId: this.story.base.id,
			cardId:
				new URLSearchParams(this.props.location.search).get(PARAMS.CARD_ID) ||
				this.story.dataByFirstCard.card?._id ||
				'',
			teamId: this.story.base.teamId,
			memberId: this.story.base.createdBy,
		});
	};

	storyComponents = () => {
		const { story } = this;

		if (!story) {
			return null;
		}

		const { cardId, ...searchRestParams } = parseURL(window.location.search).searchObject;
		const { card, step } = cardId ? story.getDataByCardId(cardId) : story.dataByFirstCard;
		const nextDefaultCard = step ? story.getNextDefaultCard(step) : undefined;
		const { mediaQuery } = story;
		const storyElements = story.data.elements;
		const storySymbols = story.symbols;
		const { isEditor } = this;
		const isEmbed = isEmbedDevPreview || (window.parent !== window && !isEditor && !isPreviewRoute);

		if (!card) {
			return (
				<NotFound
					title="Card is not found"
					actions={
						isPreviewRoute ? (
							<button type="button" onClick={() => window.location.reload()} className={css.notFoundBtn}>
								Reload
							</button>
						) : (
							<Link
								className={css.notFoundBtn}
								to={{ search: searchObjectToString(searchRestParams), pathname: STORY_PATH.base }}
							>
								Redirect
							</Link>
						)
					}
				/>
			);
		}

		return (
			<ResponsiveProvider mediaQuery={mediaQuery}>
				<SelectionHintProvider isEditor={isEditor}>
					<EventProvider
						nextDefaultCardId={nextDefaultCard?._id ?? ''}
						isEditor={isEditor}
						events={card.events}
						story={story}
					>
						<StoryElements elements={storyElements} symbols={storySymbols} isEditor={isEditor} />
						<div id={CARD_ELEMENTS_ID}>
							<CardsManager story={story} isEditor={isEditor} />
						</div>
					</EventProvider>
					{!isEditor ? <ScrollToElement id={this.props.location.hash.slice(1)} /> : null}
					{isEditor && <SelectionHint />}
					{(isEditor || isPreviewRoute) && (
						<>
							{!isEmbedDevPreview && <GlobalStyles styles="html { overflow-y: scroll; }" />}
							<CustomFonts fonts={story.settings.fonts} storycardsDomain={story.base.storycardsDomain} />
							<GoogleFonts fonts={story.settings.fonts} />
						</>
					)}
					<Credit
						isEmbed={isEmbed}
						isPreview={!!isPreview}
						isHidden={!!story.settings.restrictions?.isHideCredit || story.type === 'widget'}
					/>
				</SelectionHintProvider>

				{isPreview && (
					<React.Suspense fallback={null}>
						<ScrollbarFix />
					</React.Suspense>
				)}
			</ResponsiveProvider>
		);
	};

	render() {
		const { isReachedPlanLimit } = this.state;

		return (
			<div
				id={STORY_ROOT_ID}
				className={css.story}
				onClickCapture={isReachedPlanLimit ? undefined : this.onStoryClick}
			>
				{/* Get current story data */}
				<StoryFetcher
					story={this.props.story}
					onFetch={this.onFetch}
					history={this.props.history}
					location={this.props.location}
				/>

				{/* Story components (page content) */}
				{isReachedPlanLimit ? <InactiveStory /> : this.storyComponents()}

				{/* Connect to sdk script. Only for published story and embed preview. */}
				{!this.isEditor && (!isPreviewRoute || isEmbedDevPreview) && <EmbedingConnect />}

				{/* User info. Only published story. */}
				{!isReachedPlanLimit && !this.isEditor && !isPreviewRoute && <User />}

				{/* Preloader */}
				<Fade>
					{!this.state.story && !isReachedPlanLimit && (
						<div className={css.loader}>
							<Loader color="black" />
						</div>
					)}
				</Fade>
			</div>
		);
	}
}

export default Story;
